import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material';

import { ModelData } from '@/state/types/model-data';
import hinted from '@/components/Hinted';
import { CircularProgress } from '@/components';
import RoundedCross from '@/components/icons/RoundedCross';
import RoundedPause from '@/components/icons/RoundedPause';
import DownloadIcon from '@/components/icons/Download';
import { ModelActionButton } from './buttons';
import { ProjectData } from '@/state/types/project-data';
import BackgroundContext from '@/components/background/BackgroundContext';
import t from '@/utils/translate';


const DownloadProgress = styled(CircularProgress)({
  width: '24px',
  height: '24px',
  verticalAlign: 'middle',
  cursor: 'default'
});

const AbortDownloadButton = hinted(
  styled('button')({
    background: 'transparent',
    border: 'none',
    padding: 0,
    height: '24px',
    width: '24px',
    lineHeight: '24px',
    '&>svg': {
      verticalAlign: 'middle'
    },
    cursor: 'pointer'
  }),
  {}
);

export type Props = {
  project: ProjectData,
  item: ModelData
};

export default ({ project, item }: Props) => {
  const [ holdAbort, setHoldAbort ] = useState(false);
  const [ isHover, setIsHover ] = useState(false);

  const background = useContext(BackgroundContext);

  const [ progress, setProgress ] = useState<number | undefined>(background.modelDownloadProgress(project.code, item.id));

  const onProgress = useCallback(() => {
    setProgress(background.modelDownloadProgress(project.code, item.id));
  }, [project.code, item.id]);

  const start = useCallback(async () => {
    setHoldAbort(true);
    await background.modelDownload(project.code, item.id);
  }, [project.code, item.id]);

  const abort = useCallback(() => {
    if (isHover && !holdAbort) {
      background.abortModelDownload(project.code, item.id);
      setHoldAbort(false);
      setIsHover(false);
    }
  }, [isHover, holdAbort, project.code, item.id]);

  useEffect(() => {
    background.addEventListener('downloadProgress', onProgress);

    onProgress();

    return () => {
      background.removeEventListener('downloadProgress', onProgress);
    }
  }, [ onProgress ]);

  const mouseEnter = useCallback(() => { setIsHover(true); }, []);

  const mouseLeave = useCallback(() => {
    setHoldAbort(false);
    setIsHover(false);
  }, []);

  return (
    <>
      {!!item.sourceSize && (
        (typeof progress === 'number')
          ? (
            <AbortDownloadButton
              hint={t('project.action.abort')}
              hintDisabled={!isHover || holdAbort}
              onClick={abort}
              onMouseEnter={mouseEnter}
              onMouseLeave={mouseLeave}
            >
              {isHover && !holdAbort
                ? <RoundedCross color="#DE350B" size={18}/>
                : (!!progress
                    ? <DownloadProgress
                      variant="determinate"
                      value={progress || 0}
                      size={18}
                    />
                    : <RoundedPause color="#C1C7D0" size={20}/>
                  )
              }
            </AbortDownloadButton>
          )
          : (
            <ModelActionButton
              hint={t('model.action.downloadRVT')}
              onClick={start}
            >
              <DownloadIcon size={20} color="#091E42"/>
            </ModelActionButton>
          )
      )}
    </>
  );
}
